/**
 * DICOM Dump Dialog — display raw DICOM header tags for a study's objects.
 *
 * Opens as a read-only dialog showing the DICOM tag dump text returned by
 * getDICOMDumpInfo. The user can pick which object to inspect from a dropdown.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study, DicomObject } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';

interface Props {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const DicomDumpDialog = ({ open, study, onClose }: Props) => {
  const [objects, setObjects] = useState<DicomObject[]>([]);
  const [selectedObjKey, setSelectedObjKey] = useState('');
  const [dumpText, setDumpText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingObjects, setLoadingObjects] = useState(false);

  // Load object list when dialog opens
  useEffect(() => {
    if (!open || !study) {
      setObjects([]);
      setSelectedObjKey('');
      setDumpText('');
      return;
    }
    (async () => {
      setLoadingObjects(true);
      try {
        const result = await api.getObjects(study.objKey);
        const objs = result.records ?? [];
        setObjects(objs);
        if (objs.length > 0) {
          setSelectedObjKey(objs[0].objKey);
        }
      } catch (e) {
        pushError('Failed to load objects: ' + (e instanceof Error ? e.message : String(e)));
      } finally {
        setLoadingObjects(false);
      }
    })();
  }, [open, study]);

  // Load dump when object selection changes
  const loadDump = useCallback(async (stuKey: string, objKey: string) => {
    if (!stuKey || !objKey) return;
    setLoading(true);
    setDumpText('');
    try {
      const text = await api.getDICOMDumpInfo(stuKey, objKey);
      setDumpText(text ?? 'No DICOM data available');
    } catch (e) {
      setDumpText('Error loading DICOM dump: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && study && selectedObjKey) {
      loadDump(study.objKey, selectedObjKey);
    }
  }, [open, study, selectedObjKey, loadDump]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg, minHeight: '60vh' } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>DICOM Header Tags</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {study && (
          <Typography variant="body2" color="text.secondary">
            {study.niceName ?? study.name} — {study.acc}
          </Typography>
        )}

        {loadingObjects ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
        ) : objects.length > 0 ? (
          <FormControl size="small" fullWidth>
            <InputLabel>Object</InputLabel>
            <Select
              value={selectedObjKey}
              onChange={(e) => setSelectedObjKey(e.target.value)}
              label="Object"
            >
              {objects.map((obj) => (
                <MenuItem key={obj.objKey} value={obj.objKey}>
                  {obj.details?.sdes || obj.id} (#{obj.snum ?? '?'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" color="text.secondary">No objects found.</Typography>
        )}

        <Box sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          minHeight: '40vh',
          maxHeight: '60vh',
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Typography
              component="pre"
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0 }}
            >
              {dumpText}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
